import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { clientApi } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { X, GitCompare } from "lucide-react";

const COLORS = ["#0b5c46", "#e8a33d", "#7a5195", "#bc5090"];

function FundSlot({ index, fund, onPick, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef();

  const search = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.length < 3) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      const { data } = await clientApi.get(`/schemes?search=${encodeURIComponent(val)}&page_size=6`);
      setResults(data.items || []);
    }, 300);
  };

  if (fund) {
    return (
      <div className="flex items-center gap-2 rounded-xl border p-3" style={{ borderColor: COLORS[index] }}>
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: COLORS[index] }} />
        <span className="flex-1 truncate text-xs font-medium">{fund.scheme_name}</span>
        <button onClick={onClear} data-testid={`compare-clear-${index}`}><X className="h-3.5 w-3.5 text-[var(--ink-muted)]" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Input placeholder={`Search fund ${index + 1}...`} value={q} onChange={(e) => search(e.target.value)} data-testid={`compare-search-${index}`} />
      {results.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-[var(--glass-border)] bg-white shadow-lg">
          {results.map((r) => (
            <button key={r.scheme_code} className="block w-full truncate px-3 py-2 text-left text-xs hover:bg-[var(--surface-2)]"
              onClick={() => { onPick(r.scheme_code); setQ(""); setResults([]); }} data-testid={`compare-pick-${r.scheme_code}`}>
              {r.scheme_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ROWS = [
  ["Category", (f) => f.sub_category],
  ["Latest NAV", (f) => `₹${f.nav?.toFixed(2)} (${f.nav_date})`],
  ["Riskometer", (f) => f.riskometer],
  ["1Y Return", (f) => pct(f.trailing_returns?.["1y"])],
  ["3Y CAGR", (f) => pct(f.trailing_returns?.["3y"])],
  ["5Y CAGR", (f) => pct(f.trailing_returns?.["5y"])],
  ["Volatility (SD)", (f) => f.risk?.sd_annualized != null ? `${f.risk.sd_annualized}%` : "—"],
  ["Sharpe", (f) => f.risk?.sharpe ?? "—"],
  ["Beta (vs Nifty 50)", (f) => f.risk?.beta ?? "—"],
  ["Max Drawdown (3Y)", (f) => f.risk?.max_drawdown != null ? `${f.risk.max_drawdown}%` : "—"],
  ["Min Investment", (f) => f.transaction_metadata_verified ? formatCurrency(f.min_purchase_amt) : "Confirmed by BSE"],
  ["Inception", (f) => f.inception_date],
];

const pct = (v) => (v == null ? "—" : <span className={v >= 0 ? "text-emerald-700" : "text-red-600"}>{v > 0 ? "+" : ""}{v}%</span>);

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const [codes, setCodes] = useState(() => (params.get("codes") || "").split(",").filter(Boolean).slice(0, 4));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setParams(codes.length ? { codes: codes.join(",") } : {}, { replace: true });
    if (codes.length >= 2) {
      setLoading(true);
      clientApi.get(`/schemes-compare?codes=${codes.join(",")}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [codes]); // eslint-disable-line

  const funds = data?.funds || [];

  // merge growth series by date for overlay
  const merged = {};
  funds.forEach((f, i) => (f.growth_10k || []).forEach((p) => {
    merged[p.date] = merged[p.date] || { date: p.date };
    merged[p.date][`f${i}`] = p.value;
  }));
  const chartData = Object.values(merged).sort((a, b) => (a.date < b.date ? -1 : 1));

  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><GitCompare className="h-7 w-7" /> Compare Funds</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Side-by-side returns, risk and cost — up to 4 funds.</p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <FundSlot key={i} index={i} fund={funds[i] && codes[i] ? funds.find((f) => f.scheme_code === codes[i]) : null}
            onPick={(code) => setCodes((c) => [...c.filter((x) => x !== code), code].slice(0, 4))}
            onClear={() => setCodes((c) => c.filter((x) => x !== codes[i]))} />
        ))}
      </div>

      {codes.length < 2 && <div className="rounded-xl border border-dashed border-[var(--glass-border)] p-10 text-center text-sm text-[var(--ink-muted)]" data-testid="compare-empty">Pick at least 2 funds to compare.</div>}
      {loading && <div className="py-10 text-center text-sm text-[var(--ink-muted)]">Crunching real NAV data...</div>}

      {!loading && funds.length >= 2 && (
        <>
          <Card className="glass-card mb-6 border-0">
            <CardContent className="p-5">
              <div className="mb-2 text-sm font-medium text-[var(--brand)]">Growth of ₹10,000 (3 years)</div>
              <div className="h-64" data-testid="compare-chart">
                <ResponsiveContainer width="100%" height={256} minWidth={0}>
                  <LineChart data={chartData}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={70} />
                    <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10 }} width={55} />
                    <Tooltip formatter={(v, name) => [formatCurrency(v), funds[Number(name.slice(1))]?.scheme_name?.slice(0, 30)]} />
                    <Legend formatter={(name) => <span className="text-xs">{funds[Number(name.slice(1))]?.scheme_name?.slice(0, 32)}</span>} />
                    {funds.map((_, i) => <Line key={i} type="monotone" dataKey={`f${i}`} stroke={COLORS[i]} strokeWidth={2} dot={false} connectNulls />)}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[var(--glass-border)]">
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-[var(--glass-border)]">
                    <th className="p-3 text-left text-xs font-medium text-[var(--ink-muted)]">Metric</th>
                    {funds.map((f, i) => (
                      <th key={f.scheme_code} className="max-w-[200px] p-3 text-left text-xs font-semibold" style={{ color: COLORS[i] }}>{f.scheme_name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--glass-border)]">
                  {ROWS.map(([label, fn]) => (
                    <tr key={label}>
                      <td className="p-3 text-xs font-medium text-[var(--ink-muted)]">{label}</td>
                      {funds.map((f) => <td key={f.scheme_code} className="p-3 text-xs">{fn(f) ?? "—"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </ClientLayout>
  );
}
